/**
 * src/ui/cenario.ts
 *
 * Engine principal de renderização e orquestração do cenário interativo.
 *
 * Responsabilidades:
 * - Resolver dinamicamente a origem dos assets (CDN / ambiente local).
 * - Carregar e validar o JSON de configuração do cenário.
 * - Integrar-se ao estado do Moodle (permissões, URLs e visibilidade).
 * - Aplicar a estratégia FAIL OPEN para acessos restritos ou falhas externas.
 * - Renderizar o DOM final (imagem base, hotspots e modais).
 *
 * Observação:
 * Este arquivo concentra a lógica de alto nível do produto.
 * Ele atua como o “cérebro” que conecta dados, regras e UI.
 */

import { fetchCenarioConfig, fetchMoodleState, getCourseId } from '../core/api';
import { CenarioConfig, Hotspot, MoodleStateResponse } from '../core/types';
import { injectStyles } from './styles';
import { getErrorHtml, getLoaderHtml } from './templates';

// Prefixo global para classes CSS.
// Evita colisões com estilos do Moodle e temas externos.
const PREFIX = 'middag-app-cn';

/**
 * Extensão interna do Hotspot para uso em runtime.
 * NÃO faz parte do contrato público nem do JSON.
 */
interface HotspotInternal extends Hotspot {
  _isActivity?: boolean;
  _isLocked?: boolean;
}

/**
 * Listener interno para controle de ESC no modal.
 */
type EscListener = (e: KeyboardEvent) => void;

export class CenarioEngine {
  /** Container DOM onde o cenário será renderizado */
  private container: HTMLElement;

  /** ID do cenário (vindo de data-cenario-map) */
  private readonly configId: string;

  /** Configuração do cenário após validação */
  private config: CenarioConfig | null = null;

  /** Estado dinâmico do Moodle (permissões, URLs) */
  private moodleState: MoodleStateResponse | null = null;

  /** Indica modo debug (DEV / localhost) */
  private readonly isDebugMode: boolean = false;

  /** Base da CDN onde estão os assets */
  protected cdnBaseUrl: string = '';

  /** Listener ativo de ESC (apenas quando modal está aberto) */
  private escListener?: EscListener;

  constructor(container: HTMLElement) {
    this.container = container;
    this.configId = container.getAttribute('data-cenario-map') || '';

    // ---------------------------------------------------------------------
    // 1. Detecção de modo debug
    // ---------------------------------------------------------------------
    // Critérios:
    // - import.meta.env.DEV (Vite)
    // - hostname contendo "localhost" (fallback defensivo)
    try {
      this.isDebugMode = import.meta.env.DEV || window.location.hostname.includes('localhost');
    } catch {
      // Fallback para ambientes sem import.meta
      this.isDebugMode = window.location.hostname.includes('localhost');
    }

    // ---------------------------------------------------------------------
    // 2. Resolução dinâmica da base da CDN
    // ---------------------------------------------------------------------
    // Necessário porque, no Moodle, o script pode ser servido
    // de caminhos não previsíveis.
    this.cdnBaseUrl = this.resolveCdnBaseUrl();

    // ---------------------------------------------------------------------
    // 3. Injeção global de CSS
    // ---------------------------------------------------------------------
    // Executado uma única vez por instância.
    // Garante que os estilos do cenário estejam disponíveis.
    injectStyles();
  }

  /**
   * Descobre dinamicamente a URL base onde o bundle está hospedado.
   *
   * Problema resolvido:
   * - O Moodle frequentemente quebra caminhos relativos,
   *   causando falhas no carregamento de imagens e JSON.
   *
   * Estratégia:
   * - DEV: raiz (/)
   * - PROD: identifica a <script> que carregou app.js/app.min.js
   * - Fallback: caminho relativo ("./")
   */
  private resolveCdnBaseUrl(): string {
    // Em ambiente de desenvolvimento local, a base é a raiz
    if (this.isDebugMode) {
      return '/';
    }

    // Em produção, procura a tag <script> que carregou este código
    const scripts = document.querySelectorAll<HTMLScriptElement>('script');
    const myScript = Array.from(scripts).find(
      (s) => s.src.includes('app.min.js') || s.src.includes('app.js')
    );

    if (myScript) {
      // Remove o nome do arquivo para pegar a pasta (ex: https://cdn.../v1.0.0/)
      // Retorna: "https://s3-middag.../latest/"
      return myScript.src.substring(0, myScript.src.lastIndexOf('/') + 1);
    }

    // Fallback: tenta usar base relativa se não achar o script
    return './';
  }

  /**
   * Ponto de entrada público da engine.
   *
   * Fluxo principal:
   * 1. Renderiza loader visual
   * 2. Busca configuração estática (CDN)
   * 3. Busca estado dinâmico do Moodle (AJAX)
   * 4. Aplica merge de dados + FAIL OPEN
   * 5. Renderiza o cenário final
   */
  public async init(): Promise<void> {
    if (!this.configId) {
      this.renderError('ID do cenário não definido no atributo data-cenario-map.');
      return;
    }

    this.renderLoader();

    try {
      // Adiciona timestamp para evitar cache durante HMR/Dev
      const cacheBuster = this.isDebugMode ? `?t=${Date.now()}` : '';
      const configUrl = `${this.cdnBaseUrl}assets/configs/${this.configId}.json${cacheBuster}`;

      if (this.isDebugMode) {
        console.warn(`[CenarioEngine] Carregando config de: ${configUrl}`);
      }

      // Busca paralela para otimizar tempo de carregamento
      const [configData, moodleData] = await Promise.all([
        fetchCenarioConfig(configUrl),
        fetchMoodleState(),
      ]);

      this.config = configData;
      this.moodleState = moodleData;

      // Integra dados dinâmicos ao cenário
      this.mergeHotspotState();

      // Renderização final
      this.render();
    } catch (error) {
      console.error('[CenarioEngine] Erro fatal:', error);
      const msg = error instanceof Error ? error.message : 'Erro desconhecido';
      this.renderError(`Não foi possível carregar o cenário.<br><small>${msg}</small>`);
    }
  }

  /**
   * Aplica regras de integração Moodle e estratégia FAIL OPEN.
   *
   * Responsabilidades:
   * - Normalizar URLs relativas (imagens e assets).
   * - Resolver links dinâmicos do Moodle.
   * - Bloquear hotspots conforme permissões (`uservisible`).
   * - Garantir fallback seguro quando a API do Moodle falha.
   *
   * Importante:
   * - Esta função altera o config em memória.
   * - Após este passo, a UI assume dados prontos para renderização.
   */
  private mergeHotspotState(): void {
    if (!this.config) return;

    this.config.hotspots.forEach((h) => {
      const hotspot = h as HotspotInternal;

      // -----------------------------------------------------------------
      // Normalização de URLs de imagens
      // -----------------------------------------------------------------
      if (this.config?.image && !this.config.image.startsWith('http')) {
        this.config.image = this.toAbsoluteUrl(this.config.image);
      }

      // Normalização de URLs (Modal Imagem)
      if (hotspot.modalData?.image && !hotspot.modalData.image.startsWith('http')) {
        hotspot.modalData.image = this.toAbsoluteUrl(hotspot.modalData.image);
      }

      // Normalização de URLs (Ícone/Pin)
      if (hotspot.icon && !hotspot.icon.startsWith('http')) {
        hotspot.icon = this.toAbsoluteUrl(hotspot.icon);
      }

      // -----------------------------------------------------------------
      // Definição do link do hotspot (se for do Moodle ou customizado)
      // -----------------------------------------------------------------
      const hotspotLink = this.getHotspotLink(hotspot);
      if (hotspotLink) {
        hotspot.link = hotspot.link || { url: '', target: '_blank' }; // Garante que o objeto link exista
        hotspot.link.url = hotspotLink.url;
        if (hotspotLink.target === '_self' || hotspotLink.target === '_blank') {
          hotspot.link.target = hotspotLink.target;
        }
      }
    });
  }

  /**
   * Converte um caminho relativo em URL absoluta baseada na CDN.
   */
  private toAbsoluteUrl(path: string): string {
    // Remove barra inicial se houver para evitar //
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    return `${this.cdnBaseUrl}${cleanPath}`;
  }

  /**
   * Obter o link do hotspot
   *
   * @param hotspot
   * @private
   */
  private getHotspotLink(hotspot: HotspotInternal): { url: string; target: string } | null {
    // Caso seja o link da home do curso do Moodle
    if (hotspot.moodle?.type === 'course_home') {
      const courseId = getCourseId();
      const wwwroot = window.M?.cfg.wwwroot || '';
      if (courseId && wwwroot) {
        return { url: `${wwwroot}/course/view.php?id=${courseId}`, target: '_self' };
      }
      // Para testes locais
      if (this.isDebugMode && courseId === null && this.moodleState?.course !== undefined) {
        return { url: `/course/view.php?id=${this.moodleState.course.id}`, target: '_blank' };
      }
    }

    // Caso seja uma atividade específica do Moodle
    if (hotspot.moodle?.activityId) {
      const targetId = hotspot.moodle.activityId;
      const moodleActivity = this.moodleState?.cm.find((cm) => String(cm.id) === String(targetId));
      if (moodleActivity) {
        hotspot._isActivity = true;
        if (!moodleActivity.uservisible) {
          hotspot._isLocked = true;
          hotspot.title = `${hotspot.title} (Acesso Restrito)`;
        } else {
          return { url: moodleActivity.url, target: '_self' };
        }
      } else if (!hotspot.link?.url) {
        // Fallback genérico para atividades Moodle não encontradas
        const wwwroot = window.M?.cfg.wwwroot || '';
        if (wwwroot) {
          hotspot._isActivity = true;
          hotspot._isLocked = true;
          return { url: `${wwwroot}/mod/page/view.php?id=${targetId}`, target: '_self' };
        }
      }
    }

    // Caso seja um link customizado do hotspot
    if (hotspot.link?.url) {
      return { url: hotspot.link.url, target: hotspot.link.target || '_self' };
    }

    return null; // Se não tiver link
  }

  // ==========================================================================
  // RENDERIZAÇÃO DOM
  // ==========================================================================

  /**
   * Renderiza o cenário principal e prepara o container de modal.
   */
  private render(): void {
    if (!this.config) return;

    this.container.innerHTML = ''; // Limpa loader
    this.container.classList.add(`${PREFIX}-container`);

    // Remove estilos inline de loading que podem ter ficado
    this.container.style.minHeight = '';
    this.container.style.display = '';

    if (this.isDebugMode) {
      this.container.classList.add(`${PREFIX}-debug-mode`);
    }

    // Camada base do cenário
    const sceneLayer = document.createElement('div');
    sceneLayer.className = `${PREFIX}-scene-layer`; // Z-Index 1
    // Garante que o container siga estritamente a proporção da imagem original.
    sceneLayer.style.position = 'relative';
    sceneLayer.style.width = '100%';

    // Usa aspect-ratio moderno para travar a geometria
    if (this.config.width && this.config.height) {
      sceneLayer.style.aspectRatio = `${this.config.width} / ${this.config.height}`;
    }
    // ----------------------------------------------------------------------

    // Imagem de Fundo
    const img = document.createElement('img');
    img.src = this.config.image; // Já convertida para absoluta no mergeHotspotState
    img.className = `${PREFIX}-image-base`;
    img.alt = '';
    img.setAttribute('aria-hidden', 'true');

    // Garante que a imagem preencha o container corrigido
    img.style.width = '100%';
    img.style.height = '100%';
    img.style.objectFit = 'contain';
    img.style.display = 'block';

    img.onerror = () => {
      console.error(`[CenarioMap] Erro ao carregar imagem: ${img.src}`);
      // Poderia injetar um placeholder aqui se desejado
    };

    sceneLayer.appendChild(img);

    // Renderiza hotspots
    this.config.hotspots.forEach((h) => {
      sceneLayer.appendChild(this.createHotspotElement(h as HotspotInternal));
    });

    this.container.appendChild(sceneLayer);

    // Camada de modal (inicialmente oculta)
    this.createModalContainer();
  }

  /**
   * Cria o elemento DOM correspondente a um hotspot.
   */
  private createHotspotElement(h: HotspotInternal): HTMLElement {
    // Decide se é <a> ou <button> baseado na ação e bloqueio
    const isActivity = h._isActivity === true;
    const isLocked = h._isLocked === true;
    // Detecta se é modal de imagem ou texto
    const isModal = h.action === 'modal' || h.action === 'modal-text';
    const isUnlocked = h.unlocked || false;
    const tagName = isLocked || isModal ? 'button' : 'a';

    const el = document.createElement(tagName);

    // Classes base
    el.classList.add(`${PREFIX}-hotspot`);

    // Geometria Comum (Box ou Pin)
    const xPct = ((h.x || 0) / (this.config?.width || 1)) * 100;
    const yPct = ((h.y || 0) / (this.config?.height || 1)) * 100;

    // -----------------------------------------------------------------
    // Geometria (Box vs Polygon)
    // [LÓGICA DO PIN] Se tiver ícone, renderiza imagem e centraliza
    // -----------------------------------------------------------------
    if (h.icon) {
      // Cria a imagem do pin
      const pinImg = document.createElement('img');
      pinImg.src = h.icon;
      pinImg.alt = h.title || 'Ícone interativo';
      pinImg.className = `${PREFIX}-pin-image`; // Classe para animação de pulse
      el.appendChild(pinImg);

      // Posicionamento Centralizado (Point Model)
      el.style.left = `${xPct.toFixed(2)}%`;
      el.style.top = `${yPct.toFixed(2)}%`;
      // Move o ponto de ancoragem para o centro do ícone
      el.style.transform = 'translate(-50%, -50%)';
      // Reset de box model
      el.style.width = 'auto';
      el.style.height = 'auto';

      // Remove padding padrão de botão se houver
      el.style.background = 'none';
      el.style.border = 'none';
    } else {
      // -----------------------------------------------------------------
      // [LÓGICA PADRÃO] Hotspot Invisível (Polígono ou Retângulo)
      // -----------------------------------------------------------------

      if (h.points && h.points.length >= 3 && this.config) {
        // Lógica de Polígono (Clip-Path)
        const polygonStr = h.points
          .map(([x, y]) => {
            const px = (x / this.config!.width) * 100;
            const py = (y / this.config!.height) * 100;
            return `${px.toFixed(2)}% ${py.toFixed(2)}%`;
          })
          .join(', ');

        el.style.clipPath = `polygon(${polygonStr})`;
        el.style.width = '100%';
        el.style.height = '100%';
        el.style.top = '0';
        el.style.left = '0';

        // Expõe via CSS Custom Properties o centro geométrico (centróide) do polígono,
        // em porcentagem do canvas. O ::before consome essas variáveis para posicionar
        // o ícone (ex.: estado locked/unlocked) exatamente no centro do clip-path.
        // Também define um tamanho padrão de ícone por elemento (pode ser sobrescrito no CSS).
        const { xPctIcon, yPctIcon } = this.getPolygonCentroid(
          h.points,
          this.config.width,
          this.config.height
        );
        el.style.setProperty('--hotspot-cx', `${xPctIcon.toFixed(2)}%`);
        el.style.setProperty('--hotspot-cy', `${yPctIcon.toFixed(2)}%`);
        el.style.setProperty('--hotspot-icon-size', '1rem');
      } else if (this.config) {
        // Lógica de Retângulo (Box)
        const wPct = ((h.width || 0) / this.config.width) * 100;
        const hPct = ((h.height || 0) / this.config.height) * 100;

        el.style.left = `${xPct.toFixed(2)}%`;
        el.style.top = `${yPct.toFixed(2)}%`;
        el.style.width = `${wPct.toFixed(2)}%`;
        el.style.height = `${hPct.toFixed(2)}%`;
      }
    }

    // -----------------------------------------------------------------
    // Estilo e estado
    // -----------------------------------------------------------------
    if (h.id) {
      el.classList.add(`${PREFIX}-hotspot--${h.id}`);
    }

    if (h.variant) {
      el.classList.add(`${PREFIX}-hotspot--${h.variant}`);
    }

    if (!isUnlocked && isActivity) {
      const lockedClass = isLocked ? 'locked' : 'unlocked';
      el.classList.add(`${PREFIX}-hotspot--${lockedClass}`);
    }

    if (isLocked) {
      el.setAttribute('disabled', 'true');
      el.setAttribute('aria-disabled', 'true');
      // Garante que não será clicável quando estiver bloqueado
      el.style.pointerEvents = 'none';
    }

    if (h.customStyle) {
      el.setAttribute('style', `${el.getAttribute('style') || ''}; ${h.customStyle}`);
    }

    // -----------------------------------------------------------------
    // Acessibilidade
    // -----------------------------------------------------------------
    el.title = h.title;
    el.setAttribute('aria-label', h.ariaLabel);
    // Garante posicionamento absoluto
    el.style.position = 'absolute';

    // -----------------------------------------------------------------
    // Interações
    // -----------------------------------------------------------------
    if (!isLocked) {
      if (isModal) {
        // Ambos (Text e Image) chamam openModal
        el.addEventListener('click', () => this.openModal(h));
      } else if (tagName === 'a' && h.link) {
        el.setAttribute('href', h.link.url);
        el.setAttribute('target', h.link.target);
      }
    }

    // -----------------------------------------------------------------
    // Tooltip
    // -----------------------------------------------------------------
    // Só cria se não estiver bloqueado
    if ((h.title || h.ariaLabel) && !isLocked) {
      // Remove o título nativo para evitar o tooltip padrão do navegador
      el.removeAttribute('title');

      const tooltip = document.createElement('div');
      tooltip.className = `${PREFIX}-hotspot-tooltip`;
      tooltip.textContent = h.title || h.ariaLabel || '';

      // Estilos do tooltip
      Object.assign(tooltip.style, {
        position: 'fixed',
        backgroundColor: 'black',
        color: 'white',
        padding: '5px 10px',
        borderRadius: '5px',
        fontSize: '12px',
        zIndex: '9999',
        opacity: '0',
        pointerEvents: 'none',
        transition: 'opacity 0.2s',
        whiteSpace: 'nowrap',
        transform: 'translate(10px, 10px)',
      });

      document.body.appendChild(tooltip);

      // Evento mousemove para seguir o cursor
      el.addEventListener('mousemove', ((e: MouseEvent) => {
        tooltip.style.left = `${e.clientX}px`;
        tooltip.style.top = `${e.clientY}px`;
        tooltip.style.opacity = '1';
      }) as EventListener);

      // Evento mouseleave para esconder o tooltip
      el.addEventListener('mouseleave', () => {
        tooltip.style.opacity = '0';
      });
    }

    return el;
  }

  /**
   * Calcula o centróide (centro geométrico) de um polígono simples a partir
   * dos vértices em coordenadas absolutas (px) e retorna o resultado em
   * porcentagem do canvas (width/height).
   *
   * Observação:
   * - Usa a fórmula do centróide baseada na área (shoelace).
   * - Ideal para posicionar elementos (ex.: ::before) no centro do clip-path.
   *
   * @param points Vértices do polígono em pixels no sistema de coordenadas do cenário.
   * @param width  Largura total do cenário (px).
   * @param height Altura total do cenário (px).
   * @private
   */
  private getPolygonCentroid(
    points: number[][],
    width: number,
    height: number
  ): { xPctIcon: number; yPctIcon: number } {
    let area = 0;
    let cx = 0;
    let cy = 0;

    const len = points.length;

    for (let i = 0; i < len; i++) {
      const [x0, y0] = points[i];
      const [x1, y1] = points[(i + 1) % len];

      const cross = x0 * y1 - x1 * y0;
      area += cross;
      cx += (x0 + x1) * cross;
      cy += (y0 + y1) * cross;
    }

    area *= 0.5;

    cx /= 6 * area;
    cy /= 6 * area;

    return {
      xPctIcon: (cx / width) * 100,
      yPctIcon: (cy / height) * 100,
    };
  }

  // ==========================================================================
  // MODAL (TV EXPANDIDA & TEXTO)
  // ==========================================================================

  /**
   * Cria a estrutura base do modal.
   */
  private createModalContainer(): void {
    const layer = document.createElement('div');
    layer.className = `${PREFIX}-modal-layer`;
    layer.setAttribute('aria-hidden', 'true');
    layer.style.display = 'none'; // Starts hidden

    // Backdrop
    const backdrop = document.createElement('div');
    backdrop.className = `${PREFIX}-modal-backdrop`;
    backdrop.addEventListener('click', () => this.closeModal());

    // Content Wrapper
    const content = document.createElement('div');
    content.className = `${PREFIX}-modal-content`;
    content.id = `${PREFIX}-modal-content-wrapper`;

    // Botão Fechar Nativo
    const closeBtn = document.createElement('button');
    closeBtn.className = `${PREFIX}-modal-close-btn`;
    closeBtn.innerHTML = '✕';
    closeBtn.setAttribute('aria-label', 'Fechar modal');
    closeBtn.addEventListener('click', () => this.closeModal());

    layer.append(backdrop, content, closeBtn);
    this.container.appendChild(layer);
  }

  /**
   * Abre um modal baseado no hotspot de origem.
   */
  private openModal(sourceHotspot: HotspotInternal): void {
    const layer = this.container.querySelector<HTMLElement>(`.${PREFIX}-modal-layer`);
    const content = this.container.querySelector<HTMLElement>(`.${PREFIX}-modal-content`);

    if (!layer || !content) return;

    // Limpa conteúdo anterior
    content.innerHTML = '';
    content.style.aspectRatio = ''; // Reset
    content.style.maxWidth = ''; // Reset

    // =================================================================
    // OPÇÃO A: MODAL DE TEXTO
    // =================================================================
    if (
      sourceHotspot.action === 'modal-text' ||
      (sourceHotspot.content && !sourceHotspot.modalData)
    ) {
      content.classList.add(`${PREFIX}-modal-content--text-mode`);
      content.style.width = '100%';
      content.style.maxWidth = '600px'; // Largura de leitura confortável
      content.style.backgroundColor = 'white';
      content.style.borderRadius = '8px';
      content.style.padding = '2rem';
      content.style.position = 'relative';

      const title = document.createElement('h2');
      title.className = `${PREFIX}-modal-title`;
      title.textContent = sourceHotspot.title;

      const bodyText = document.createElement('div');
      bodyText.className = `${PREFIX}-modal-text-body`;
      // Sanitização básica seria ideal aqui, mas usando innerHTML conforme requisito
      bodyText.innerHTML = sourceHotspot.content || '<p>Conteúdo não disponível.</p>';

      content.appendChild(title);
      content.appendChild(bodyText);
    }
    // =================================================================
    // OPÇÃO B: MODAL DE IMAGEM (TV / CENÁRIO RECURSIVO)
    // =================================================================
    else if (sourceHotspot.modalData) {
      content.classList.remove(`${PREFIX}-modal-content--text-mode`);
      content.style.backgroundColor = 'transparent';
      // Container do modal também deve respeitar aspect-ratio se houver dados
      content.style.width = '90%'; // fallback
      content.style.maxWidth = '1000px'; // limite razoável

      if (sourceHotspot.modalData.width && sourceHotspot.modalData.height) {
        content.style.aspectRatio = `${sourceHotspot.modalData.width} / ${sourceHotspot.modalData.height}`;
      }

      const img = document.createElement('img');
      img.src = sourceHotspot.modalData.image.startsWith('http')
        ? sourceHotspot.modalData.image
        : this.toAbsoluteUrl(sourceHotspot.modalData.image);

      img.className = `${PREFIX}-image-base`;
      img.style.width = '100%';
      img.style.height = '100%';
      img.style.objectFit = 'contain';

      content.appendChild(img);

      // Hotspots do Modal - Renderiza Hotspots internos
      sourceHotspot.modalData.hotspots.forEach((subH) => {
        // Recalcula geometria baseado no tamanho do modal (1920x1080 geralmente)
        // Precisamos mockar um config temporário para o createHotspotElement funcionar
        const tempEngine = new CenarioEngine(document.createElement('div'));
        tempEngine.config = sourceHotspot.modalData ?? null;
        // Hack para passar a CDN
        // @ts-ignore
        tempEngine.cdnBaseUrl = this.cdnBaseUrl;

        const subEl = tempEngine.createHotspotElement(subH as HotspotInternal);

        // Obtem o link do hotspot
        const subHotspotLink = this.getHotspotLink(subH as HotspotInternal);
        if (subHotspotLink) {
          subEl.setAttribute('href', subHotspotLink.url);
          subEl.setAttribute('target', subHotspotLink.target);
        }

        // Tratamento especial para botão "Voltar" dentro do modal
        // Se a ação não for definida ou for navigate para #, assume fechar
        if (!subH.link && !subH.action) {
          subEl.addEventListener('click', () => this.closeModal());
        }

        content.appendChild(subEl);
      });
    } else {
      return; // Nada para abrir
    }

    // 2. Animação de Zoom (FLIP technique simplificada)
    layer.style.display = 'flex';
    void layer.offsetWidth; // Force Reflow

    layer.classList.add(`${PREFIX}-modal-layer--active`);
    layer.setAttribute('aria-hidden', 'false');

    // Focus Trap simples: foca no botão fechar
    const closeBtn = layer.querySelector<HTMLElement>(`.${PREFIX}-modal-close-btn`);
    if (closeBtn) {
      closeBtn.focus(); // Garante o foco no botão de fechar
    }

    // Listener de ESC
    this.escListener = (e: KeyboardEvent) => {
      if (e.key === 'Escape') this.closeModal();
    };

    document.addEventListener('keydown', this.escListener);
  }

  /**
   * Fecha o modal ativo.
   */
  private closeModal(): void {
    const layer = this.container.querySelector<HTMLElement>(`.${PREFIX}-modal-layer`);
    if (!layer) return;

    layer.classList.remove(`${PREFIX}-modal-layer--active`);
    layer.setAttribute('aria-hidden', 'true');

    if (this.escListener) {
      document.removeEventListener('keydown', this.escListener);
      this.escListener = undefined;
    }

    setTimeout(() => {
      layer.style.display = 'none';
      // Limpa estilo inline que pode ter sido aplicado pelo tipo de modal
      const content = this.container.querySelector<HTMLElement>(`.${PREFIX}-modal-content`);
      if (content) content.setAttribute('style', '');
    }, 300);
  }

  // ==========================================================================
  // FEEDBACK VISUAL
  // ==========================================================================

  private renderLoader(): void {
    this.container.innerHTML = getLoaderHtml();
  }

  private renderError(msg: string): void {
    this.container.innerHTML = getErrorHtml(msg);
  }
}
